import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useState } from 'react';
import { apiFetch } from '../lib/api';
import { useCart, type ShelfItem } from '../lib/cart';
import { formatAud, formatPack, formatPerUnit, formatUnits } from '../lib/format';
import { Badge, Button, Empty, PageHeader, Spinner } from '../components/ui';

interface Pack {
  packUnits: number;
  packPriceCents: number;
}

interface Item {
  id: string;
  name: string;
  emoji: string;
  description: string | null;
  unitPriceCents: number;
  availableUnits: number;
  packs: Pack[];
  customerPriced: boolean;
  minPriceCents: number | null;
  maxPriceCents: number | null;
  /*
   * Staff-only fields. The items endpoint deliberately shows staff everything,
   * including what has been switched off, so a deactivated line can be found and
   * fixed rather than vanishing. But an owner browsing the shop then sees stock a
   * customer cannot, with nothing to say so — which reads as "the retired
   * products are still on sale". These two flags are what the badge below needs
   * to tell the two states apart.
   */
  isActive?: boolean;
  vegetableActive?: boolean;
}

/**
 * A pack, restated as the offer a customer weighs up.
 *
 * The saving is against buying that many singly, which is the only comparison
 * that means anything: the pack price is set independently, so there is no
 * "normal rate" for ten to be discounted from. Ten singles is $200, the pack is
 * $150, so the pack saves $50 — that sentence is the whole deal.
 */
function describePack(pack: Pack, unitPriceCents: number) {
  return {
    ...pack,
    savingCents: pack.packUnits * unitPriceCents - pack.packPriceCents,
  };
}

function dealsFor(item: Item) {
  return item.packs
    .map((pack) => describePack(pack, item.unitPriceCents))
    // A pack dearer than the singles it replaces never fires in the engine, so
    // showing it would advertise a saving the customer would not receive.
    .filter((deal) => deal.savingCents > 0)
    .sort((a, b) => a.packUnits - b.packUnits);
}

const centsFromInput = (value: string) => Math.round(Number.parseFloat(value || '0') * 100);

export function Catalog() {
  const { storeId = '' } = useParams();
  const [receipt, setReceipt] = useState<{ reference: string; totalCents: number } | null>(null);
  // What the buyer has typed into each "name your price" box, before adding.
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});

  const items = useQuery({
    queryKey: ['items', storeId],
    queryFn: () => apiFetch<Item[]>(`/api/stores/${storeId}/items`),
  });

  // The shelf doubles as the price book: handing it to the cart is what lets a
  // tap be priced in the browser instead of waiting for the server to say.
  const shelf: ShelfItem[] = (items.data ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    emoji: item.emoji,
    unitPriceCents: item.unitPriceCents,
    availableUnits: item.availableUnits,
    packs: item.packs,
    customerPriced: item.customerPriced,
    minPriceCents: item.minPriceCents,
    maxPriceCents: item.maxPriceCents,
  }));

  const { cart, problem, adjust, removeAll, buy } = useCart(storeId, shelf);

  if (items.isPending) return <Spinner label="Reading the shelves" />;
  if (!items.data?.length) {
    return (
      <Empty title="Nothing stocked here yet" body="Try another store while this one fills up." />
    );
  }

  const quantityFor = (storeItemId: string) =>
    cart.lines.find((line) => line.storeItemId === storeItemId)?.quantityUnits ?? 0;

  const onDeal = items.data.filter(
    (item) =>
      dealsFor(item).length > 0 && item.isActive !== false && item.vegetableActive !== false,
  ).length;

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_21rem] lg:items-start">
      <section>
        <PageHeader
          title="On the shelf"
          subtitle="Add one at a time, or take a whole pack. The best price is worked out for you — a pack applies the moment you have enough for it."
          action={
            onDeal > 0 ? (
              <span className="ribbon">
                {onDeal} deal{onDeal === 1 ? '' : 's'} on
              </span>
            ) : undefined
          }
        />

        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          {items.data.map((item, index) => {
            const inCart = quantityFor(item.id);
            const soldOut = item.availableUnits <= 0;
            const deals = dealsFor(item);
            const biggest = deals.at(-1);
            // Only ever true for staff: customers are never sent these rows.
            const hidden = item.isActive === false || item.vegetableActive === false;

            return (
              <article
                key={item.id}
                className={`tag rise flex flex-col p-5 ${deals.length > 0 && !hidden ? 'on-deal' : ''} ${
                  hidden ? 'opacity-55' : ''
                }`}
                style={{ animationDelay: `${index * 35}ms` }}
              >
                <div className="flex items-start gap-3">
                  <span className="medallion" aria-hidden>
                    {item.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg leading-tight text-ink-950">{item.name}</h2>
                      {hidden ? (
                        <Badge tone="danger">Hidden from customers</Badge>
                      ) : (
                        deals.length > 0 && <span className="ribbon">Deal</span>
                      )}
                      {soldOut && <Badge>Sold out</Badge>}
                    </div>
                    <p className="tabular mt-1 text-[15px] text-ink-900">
                      {item.customerPriced ? 'You name the price' : formatPerUnit(item.unitPriceCents)}
                    </p>
                    {!soldOut && (
                      <p className="tabular text-[12px] text-ink-500">
                        {item.availableUnits} in stock
                      </p>
                    )}
                  </div>
                </div>

                {item.description && (
                  <p className="mt-2.5 text-[13.5px] leading-snug text-ink-500">
                    {item.description}
                  </p>
                )}

                {item.customerPriced && (
                  <p className="tabular mt-3.5 border-t hairline pt-3 text-[12.5px] text-ink-500">
                    Pay anything from {formatAud(item.minPriceCents ?? 0)} to{' '}
                    {formatAud(item.maxPriceCents ?? 0)} each.
                  </p>
                )}

                {deals.length > 0 && (
                  <ul className="mt-3.5 space-y-1.5 border-t hairline pt-3">
                    {deals.map((deal) => (
                      <li key={deal.packUnits} className="flex items-baseline justify-between gap-2">
                        <span className="tabular text-[13.5px] text-ink-900">
                          {formatPack(deal.packUnits, deal.packPriceCents)}
                        </span>
                        <span className="sign text-[10px] text-ochre-600">
                          save {formatAud(deal.savingCents)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {item.customerPriced ? (
                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                    <div className="flex items-center rounded-[3px] border border-paper-300 bg-paper-50 px-2">
                      <span className="tabular text-[13px] text-ink-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min={(item.minPriceCents ?? 0) / 100}
                        max={(item.maxPriceCents ?? 0) / 100}
                        aria-label={`Your price for one ${item.name}`}
                        className="tabular w-20 bg-transparent px-1 py-1.5 text-[14px] text-ink-900 outline-none"
                        value={priceDrafts[item.id] ?? (item.unitPriceCents / 100).toFixed(2)}
                        onChange={(event) =>
                          setPriceDrafts((state) => ({ ...state, [item.id]: event.target.value }))
                        }
                      />
                    </div>
                    <Button
                      variant="primary"
                      disabled={soldOut || inCart >= item.availableUnits}
                      onClick={() =>
                        adjust(
                          item.id,
                          1,
                          centsFromInput(
                            priceDrafts[item.id] ?? (item.unitPriceCents / 100).toFixed(2),
                          ),
                        )
                      }
                    >
                      Add
                    </Button>
                    {inCart > 0 && (
                      <Button
                        variant="quiet"
                        aria-label={`Remove one ${item.name}`}
                        onClick={() => adjust(item.id, -1)}
                      >
                        −1
                      </Button>
                    )}
                    <span className="tabular ml-auto text-[14px] text-ink-700">
                      {inCart > 0 ? formatUnits(inCart) : ''}
                    </span>
                  </div>
                ) : (
                <div className="mt-auto flex items-center gap-2 pt-4">
                  <Button
                    variant="quiet"
                    aria-label={`Remove one ${item.name}`}
                    disabled={inCart <= 0}
                    onClick={() => adjust(item.id, -1)}
                  >
                    −1
                  </Button>
                  <Button
                    variant="primary"
                    aria-label={`Add one ${item.name}`}
                    disabled={soldOut || inCart >= item.availableUnits}
                    onClick={() => adjust(item.id, 1)}
                  >
                    +1
                  </Button>
                  {biggest && (
                    <Button
                      variant="primary"
                      aria-label={`Add a pack of ${biggest.packUnits} ${item.name}`}
                      disabled={soldOut || inCart + biggest.packUnits > item.availableUnits}
                      onClick={() => adjust(item.id, biggest.packUnits)}
                    >
                      +{biggest.packUnits}
                    </Button>
                  )}
                  <span className="tabular ml-auto text-[14px] text-ink-700">
                    {inCart > 0 ? formatUnits(inCart) : ''}
                  </span>
                </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <aside className="board sticky top-24 p-5">
        <h2 className="sign text-[11px] tracking-[0.18em] text-chalk/60">Basket</h2>

        {receipt && (
          <div className="mt-4 rounded-[3px] border board-rule border p-3">
            <p className="sign text-[10px] text-leaf-300">Bought</p>
            <p className="tabular mt-1 text-lg text-chalk">{formatAud(receipt.totalCents)}</p>
            <p className="mt-1 text-[12px] text-chalk/55">
              Quote <span className="tabular">{receipt.reference}</span> at the counter.
            </p>
          </div>
        )}

        {problem && (
          <p className="mt-4 rounded-[3px] border-l-[3px] border-tomato-500 bg-tomato-500/12 px-3 py-2 text-[13px] text-chalk">
            {problem}
          </p>
        )}

        {cart.lines.length === 0 ? (
          <p className="mt-4 text-[13.5px] leading-relaxed text-chalk/50">
            Nothing yet. Tap +1 on anything to start.
          </p>
        ) : (
          <>
            <ul className="mt-4 space-y-3">
              {cart.lines.map((line) => (
                <li
                  key={line.storeItemId}
                  className="border-b board-rule border-b pb-3 last:border-0"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-[14px] text-chalk">
                      <span className="mr-1.5" aria-hidden>
                        {line.emoji}
                      </span>
                      {line.name}
                    </span>
                    <span className="tabular text-[14px] text-chalk">
                      {formatAud(line.lineTotalCents)}
                    </span>
                  </div>

                  {/* What the line is actually made of, rather than an average
                      price per unit that nobody was charged. */}
                  {line.customerPriced && (
                    <p className="sign mt-1 text-[9px] text-ochre-400">
                      your price · {formatAud(line.unitPriceCents)} each
                    </p>
                  )}

                  {line.packsApplied.length > 0 && (
                    <p className="sign mt-1 text-[9px] text-ochre-400">
                      {line.packsApplied
                        .map((pack) => `${pack.count} × ${pack.packUnits}-pack`)
                        .join(' + ')}
                      {line.looseUnits > 0 && ` + ${line.looseUnits} single`}
                      {line.lineDiscountCents > 0 &&
                        ` · saved ${formatAud(line.lineDiscountCents)}`}
                    </p>
                  )}

                  {/* Two explicit actions: take one off, or clear the line.
                      Emptying a 23-unit line used to mean twenty-three taps. */}
                  <div className="mt-2 flex items-center gap-1.5">
                    <button
                      type="button"
                      aria-label={`Remove one ${line.name}`}
                      className="h-6 w-6 rounded-[2px] bg-chalk/10 text-[13px] text-chalk hover:bg-chalk/20"
                      onClick={() => adjust(line.storeItemId, -1)}
                    >
                      −
                    </button>
                    <span className="tabular min-w-8 text-center text-[13px] text-chalk">
                      {line.quantityUnits}
                    </span>
                    <button
                      type="button"
                      aria-label={`Add one ${line.name}`}
                      className="h-6 w-6 rounded-[2px] bg-chalk/10 text-[13px] text-chalk hover:bg-chalk/20 disabled:opacity-40"
                      disabled={line.quantityUnits >= line.availableUnits}
                      onClick={() => adjust(line.storeItemId, 1)}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="sign ml-auto text-[9px] text-chalk/45 underline-offset-2 hover:text-tomato-500 hover:underline"
                      onClick={() => removeAll(line.storeItemId)}
                    >
                      Remove all
                    </button>
                  </div>

                  {/*
                   * The pack cliff, surfaced as an offer. Nine units genuinely
                   * costs more than ten; a customer who finds that out
                   * afterwards feels tricked, so we say it up front.
                   */}
                  {line.opportunity && (
                    <button
                      type="button"
                      className="sign mt-2 w-full rounded-[2px] bg-ochre-500/20 px-2 py-1.5 text-left text-[10px] text-ochre-400 hover:bg-ochre-500/30"
                      onClick={() => adjust(line.storeItemId, line.opportunity?.extraUnits ?? 0)}
                    >
                      Add {line.opportunity.extraUnits} → save{' '}
                      {formatAud(line.opportunity.savingCents)}
                    </button>
                  )}

                  {line.exceedsStock && (
                    <p className="sign mt-2 text-[10px] text-tomato-500">
                      Only {line.availableUnits} left
                    </p>
                  )}
                </li>
              ))}
            </ul>

            <dl className="mt-4 space-y-1 border-t board-rule border-t pt-4 text-[13px]">
              <div className="flex justify-between text-chalk/55">
                <dt>Subtotal</dt>
                <dd className="tabular">{formatAud(cart.subtotalCents)}</dd>
              </div>
              {cart.discountCents > 0 && (
                <div className="flex justify-between text-ochre-400">
                  <dt>Deals</dt>
                  <dd className="tabular">−{formatAud(cart.discountCents)}</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between pt-1 text-chalk">
                <dt className="sign text-[11px]">Total</dt>
                <dd className="tabular text-2xl">{formatAud(cart.totalCents)}</dd>
              </div>
            </dl>

            <Button
              variant="primary"
              className="mt-4 w-full py-2.5"
              disabled={buy.isPending || cart.lines.some((line) => line.exceedsStock)}
              onClick={() => buy.mutate(undefined, { onSuccess: (data) => setReceipt(data) })}
            >
              {buy.isPending ? 'Buying…' : 'Buy now'}
            </Button>
            <p className="mt-2 text-[11px] leading-snug text-chalk/40">
              Stock is taken immediately. Pay at the counter when you collect.
            </p>
          </>
        )}
      </aside>
    </div>
  );
}
